/**
 * Search, folded into the rankings controls.
 *
 * WHY IT LIVES HERE
 * -----------------
 * Search used to be a top-level tab, which put it in competition with the
 * rankings for the same job. It is not a separate destination — it is a way of
 * narrowing the thing you are already looking at. So the control sits in the
 * row of controls, alongside category and audience, and opens in place.
 *
 * THE EXPANSION
 * -------------
 * The button does not swap for an input; it GROWS into one, and the magnifier
 * you pressed stays put and becomes the submit. That continuity is what makes
 * it read as one control rather than two, and it is what tells you how to get
 * back: the same shape shrinks again.
 *
 * Escape closes, so does clicking away while empty, so does the ×. Any of the
 * three returns focus to the button you started from — leaving focus stranded
 * in a control that is no longer visible is the usual way expanding search gets
 * this wrong.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function InlineSearch({
  /** Prefilled when returning from a search, so the term is still there. */
  initialQuery = '',
}: {
  initialQuery?: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(initialQuery);

  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const inputId = useId();

  // Focus follows the expansion, so typing can start immediately. Waiting a
  // frame lets the width transition begin first; focusing mid-layout makes
  // mobile browsers scroll the row around.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [open]);

  const close = (restoreFocus: boolean): void => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const submit = (): void => {
    const q = draft.trim();
    if (q.length === 0) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <form
      ref={formRef}
      className={`pick-find${open ? ' is-open' : ''}`}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setDraft('');
          close(true);
        }
      }}
      onBlur={(e) => {
        // Only when focus has genuinely left the control, and only when there
        // is nothing to lose. A half-typed term must survive a stray tap.
        if (!open || draft.trim().length > 0) return;
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setOpen(false);
      }}
    >
      <div className="pick-find-shell">
        <button
          ref={triggerRef}
          type={open ? 'submit' : 'button'}
          className="pick-find-go"
          aria-expanded={open}
          aria-controls={inputId}
          aria-label={open ? 'Search' : 'Search products'}
          title={open ? 'Search' : 'Search products'}
          onClick={() => {
            if (!open) setOpen(true);
          }}
        >
          <span aria-hidden="true">⌕</span>
        </button>

        <label className="visually-hidden" htmlFor={inputId}>
          Search products
        </label>
        <input
          id={inputId}
          ref={inputRef}
          className="pick-find-input"
          type="text"
          value={draft}
          placeholder="Search products…"
          autoComplete="off"
          maxLength={120}
          // Out of the tab order while collapsed: it is visually gone, and a
          // hidden stop on the way to the next control is disorienting.
          tabIndex={open ? 0 : -1}
          aria-hidden={!open}
          onChange={(e) => setDraft(e.target.value)}
        />

        {open && (
          <button
            type="button"
            className="pick-find-close"
            aria-label="Close search"
            title="Close search"
            onClick={() => {
              setDraft('');
              close(true);
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
    </form>
  );
}
